
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import LookupNameIds from '@/store/enums/settingsEnums';
  import PackageValidationRuleFilter from '@/components/dropdown/PackageValidationRuleFilter.vue';

  export default defineComponent({
    name: 'package-validation-rule-listing',
    components: {
      Datatable,
      PackageValidationRuleFilter,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const formatRuleType = (type: string) => {
        const types = {
          CLASS_CATEGORY: translate('classCategory'),
          PACKAGE_COUNT: translate('packageCount'),
          HORSE_LESSON_COMBO: translate('horseLessonCombo'),
          INVALID_PACKAGE: translate('invalidPackage'),
        };
        return types[type] || type;
      };

      const applyFilters = async (filters: any) => {
        const cleanFilters: any = {};
        if (filters.ruleType) cleanFilters.ruleType = filters.ruleType;
        if (filters.isActive)
          cleanFilters.isActive = filters.isActive === 'true';
        if (filters.classCategoryId)
          cleanFilters.classCategoryId = filters.classCategoryId;
        if (filters.horseTypeId) cleanFilters.horseTypeId = filters.horseTypeId;
        if (filters.lessonTypeId)
          cleanFilters.lessonTypeId = filters.lessonTypeId;

        await store.dispatch(
          Actions.UPDATE_PACKAGE_VALIDATION_RULE_FILTER,
          cleanFilters
        );
        await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULES);
      };

      const clearFilters = async () => {
        await store.dispatch(Actions.RESET_PACKAGE_VALIDATION_RULE_STORE);
        await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULES);
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('packageValidationRules'), [
          translate('settings'),
        ]);

        onBeforeUnmount(() => {
          store.dispatch(Actions.RESET_PACKAGE_VALIDATION_RULE_STORE);
        });

        loading.value = true;
        try {
          // Load dropdown data
          await store.dispatch(Actions.GET_ALL_SETTINGS_CLASS_CATEGORY);
          await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
            LookupNameIds.HORSE_TYPE,
            LookupNameIds.CLASS_TYPE,
          ]);
          // Load validation rules
          await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULES);
        } catch (error) {
          console.error('Error loading package validation rules:', error);
        } finally {
          loading.value = false;
        }
      });

      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('name'),
          key: 'name',
          sortable: true,
        },
        {
          name: translate('ruleType'),
          key: 'ruleType',
          sortable: true,
        },
        {
          name: translate('priority'),
          key: 'priority',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'isActive',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const deleteRule = async (rule) => {
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_PACKAGE_VALIDATION_RULE, rule.id);
          await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULES);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          }
        }
      };

      const editRule = (id) => {
        router.push({
          name: 'package-validation-rule-editing',
          params: { id },
        });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(
          Actions.UPDATE_PACKAGE_VALIDATION_RULE_CURRENT_PAGE,
          val
        );
        await store.dispatch(Actions.GET_PACKAGE_VALIDATION_RULES);
        loading.value = false;
      };

      return {
        tableData: computed(() => store.getters.packageValidationRuleList),
        tableHeader,
        deleteRule,
        translate,
        totalItems: computed(
          () => store.getters.packageValidationRuleListCount
        ),
        pageChanged,
        goTo,
        editRule,
        loading,
        can,
        applyFilters,
        clearFilters,
        formatRuleType,
      };
    },
  });
